/**
 * Preview FAQ card with in-place expand / collapse for the rest.
 *
 * Belongs here: FAQ list UI, expand state, view-all / show-less toggle.
 * Does not belong here: plan footer chrome (risk profile, disclaimer).
 */
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { FAQItem } from '../../../../../../components/ui-components/FAQItem'
import { EventNames } from '../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../utils/appBridge'

export type PlansFaq = {
  title: string
  description: string
}

type PlansFaqsSectionProps = {
  faqs: PlansFaq[]
  maxVisibleFaqs: number
  screenName: string
  faqAccordionEventId: string
  faqViewAllEventId: string
}

export function PlansFaqsSection({
  faqs,
  maxVisibleFaqs,
  screenName,
  faqAccordionEventId,
  faqViewAllEventId
}: PlansFaqsSectionProps) {
  const [showAllFaqs, setShowAllFaqs] = useState(false)
  const previewFaqs = faqs.slice(0, maxVisibleFaqs)
  const remainingFaqs = faqs.slice(maxVisibleFaqs)
  const hasMoreFaqs = remainingFaqs.length > 0

  function handleToggleShowAllFaqs() {
    lightImpactVibrate()
    const nextShowAllFaqs = !showAllFaqs
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: screenName,
        eventId: faqViewAllEventId,
        buttonLabel: nextShowAllFaqs ? 'View all FAQs' : 'Show less'
      }
    })
    setShowAllFaqs(nextShowAllFaqs)
  }

  return (
    <div className="mt-8 rounded-[12px] overflow-hidden bg-white">
      <PlansFaqsList
        faqs={previewFaqs}
        screenName={screenName}
        faqAccordionEventId={faqAccordionEventId}
      />
      <AnimatePresence initial={false}>
        {showAllFaqs && hasMoreFaqs ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden border-t border-[#E7EAED]"
          >
            <PlansFaqsList
              faqs={remainingFaqs}
              screenName={screenName}
              faqAccordionEventId={faqAccordionEventId}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
      {hasMoreFaqs ? (
        <div
          className="w-full text-left px-4 pt-3 pb-4 text-[13px] font-[500] text-[#232323] underline 
           border-t border-[#E7EAED] cursor-pointer rounded-none"
          onClick={handleToggleShowAllFaqs}>{showAllFaqs ? 'Show less' : "View all FAQ's"}</div>
      ) : null}
    </div>
  );
}

function PlansFaqsList({ faqs, screenName, faqAccordionEventId, accordionHeaderClassName, accordionBodyClassName }: {
  faqs: PlansFaq[]
  screenName: string
  faqAccordionEventId: string
  accordionHeaderClassName?: string
  accordionBodyClassName?: string
}) {
  const [expandedFaqState, setExpandedFaqState] = useState<Record<string, boolean>>({})

  return (
    <>
      {faqs.map((faqItem, faqIndex) => (
        <div
          key={faqItem.title}
          className={faqIndex > 0 ? 'border-t border-[#E7EAED]' : undefined}
        >
          <FAQItem
            title={faqItem.title}
            description={faqItem.description}
            isExpanded={expandedFaqState[faqItem.title] ?? false}
            accordionHeaderClassName={accordionHeaderClassName}
            accordionBodyClassName={accordionBodyClassName}
            onAccordionClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: screenName,
                  eventId: faqAccordionEventId,
                  buttonLabel: faqItem.title
                }
              })
              setExpandedFaqState((previousState) => ({
                ...previousState,
                [faqItem.title]: !previousState[faqItem.title]
              }))
            }}
          />
        </div>
      ))}
    </>
  )
}
